import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { isCveScript } from './cveSummary';
import { SYSINFO_FIELDS, isSystemInfoScript } from './systemInfoSummary';
import { SUMMARY_FIELDS, isSummaryScript } from './updateSummary';

// The data behind the Endpoint Devices summary: for one account and device type, every machine's Windows updates
// status and its known-vulnerability (CVE) status. Both come from the latest result of a script the VES schedule
// for that account and device type runs. Only a few fields of each result are read (VES fields=...), enough for the
// tiles and the machine lists; a machine's whole result is loaded when its own page is opened.
//
// The address of the results of one script (or, without a script, the schedules and the scripts they run).
export const resultsUrl = (account, deviceType, script, fields) => {
  const q = new URLSearchParams({ account, deviceType });
  if (script) q.set('script', script);
  if (fields) q.set('fields', fields.join(','));
  return `cma/endpointresult?${q.toString()}`;
};

// The known vulnerabilities (CVEs) of every machine of an account. The API looks them up from each machine's stored
// installed programs (the script only reports what is installed) and answers with the flat fields the page reads.
export const cveUrl = (account, deviceType) => {
  const q = new URLSearchParams({ account, deviceType });
  return `cma/endpointcve?${q.toString()}`;
};

const EMPTY = { name: '', items: [], note: '' };

export function useEndpointSummary(accountCode, deviceType) {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const requestId = useRef(0);

  const [state, setState] = useState({ loading: false, error: null, scripts: [], updates: EMPTY, cve: EMPTY, sysinfo: EMPTY });

  const load = useCallback(async () => {
    if (!accountCode) return;
    const id = ++requestId.current;
    setState(prev => ({ ...prev, loading: true, error: null }));

    // One script's latest result on every machine, selected fields only. A script that is not scheduled or cannot
    // be read leaves a note for its card and never stops the other one.
    const read = async (name, fields, notScheduled, failed, url) => {
      if (!name) return { ...EMPTY, note: notScheduled };
      try {
        const response = await apiRef.current.request('GET', url || resultsUrl(accountCode, deviceType, name, fields));
        return { name, items: Array.isArray(response.data?.items) ? response.data.items : [], note: '' };
      } catch (ex) {
        console.error(`Failed to load the ${name} results:`, ex);
        return { name, items: [], note: failed };
      }
    };

    try {
      const response = await apiRef.current.request('GET', resultsUrl(accountCode, deviceType));
      const schedules = Array.isArray(response.data?.schedules) ? response.data.schedules : [];
      const scripts = [...new Set(schedules.flatMap(s => s.scripts || []))];

      const [updates, cve, sysinfo] = await Promise.all([
        read(scripts.find(isSummaryScript), SUMMARY_FIELDS,
          'The Windows updates summary is not scheduled for this account and device type.',
          'Could not load the Windows updates summary.'),
        read(scripts.find(isCveScript), undefined,
          'The installed-programs script (get-installedprograms) is not scheduled for this account and device type.',
          'Could not load the vulnerability summary.', cveUrl(accountCode, deviceType)),
        read(scripts.find(isSystemInfoScript), SYSINFO_FIELDS,
          'The system info script (get-systeminfo) is not scheduled for this account and device type.',
          'Could not load the replacement and age summary.'),
      ]);
      if (id !== requestId.current) return;
      setState({ loading: false, error: null, scripts, updates, cve, sysinfo });
    } catch (ex) {
      if (id !== requestId.current) return;
      console.error('Failed to load the endpoint summary:', ex);
      setState({ loading: false, error: ex?.toString?.() || 'Failed to load the endpoint summary', scripts: [], updates: EMPTY, cve: EMPTY, sysinfo: EMPTY });
    }
  }, [accountCode, deviceType]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}
